import React from 'react';
import IframeViewer from './IframeViewer';
import VideoPlayer from './VideoPlayer';
import VideoPlayerEndSlate from './VideoPlayerEndSlate';
import AssetLink from './AssetLink';

require('../../css/components/AssetDetails.scss');

'use strict';

const MetadataContainer = ({ children, embedded, copy, asset }) => (
    <div className={"asset-metadata-container" + (embedded ? " embedded" : "")}>
        {embedded ? (
            <button
                className="info-button"
                aria-expanded={true}
                aria-controls="assetMetadata-collapse"
                type="button"
                data-target="#assetMetadata-collapse"
                data-toggle="collapse"
                title={copy.aboutThisVideo} />
        ) : null}
        <div
            id="assetMetadata-collapse"
            className={"asset-metadata" + (embedded ? " collapse" : "")}
        >
            <div id="assetMetadata" className="description-languages-transcripts-share">
                {children}
                {embedded ? (
                    <div>
                        <h2>{copy.title}</h2>
                        <p>{asset.title}</p>
                    </div>
                ) : null}
            </div>
        </div>
    </div>
);

const AssetHeader = ({ children, asset, copy, embedded, languages }) => (
    <>
        <div className="heading">{children}</div>
        <MetadataContainer embedded={embedded} asset={asset} copy={copy}>
            {asset.description && (
                <div className={embedded ? "my-1" : "my-3"}>
                    <p> {asset.description} </p>
                </div>
            )}
            <div className="d-flex flex-row justify-content-between align-items-center">
                <div className="d-flex flex-row flex-wrap languages">
                    <h2 className="mt-2 mb-0">{copy.availableLanguages}:</h2>
                    <div className="d-flex flex-row flex-wrap mt-2 mb-0">{languages}</div>
                </div>
                <div className="share pb-2">
                    <a
                        className="shareButton"
                        href="#"
                        data-toggle="modal"
                        data-target="#share-dialog"
                    >
                        {copy.share}
                    </a>
                </div>
            </div>
        </MetadataContainer>
    </>
);

export default class AssetDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = { languageUrls: props.languageUrls, asset: props.asset, loaded: props.asset !== null, loading: false, xmlContent: "", isLoadingNewContent: false };

        this.setPlayerRef = this.setPlayerRef.bind(this);
        this.getBestLanguage = this.getBestLanguage.bind(this);
        this.sendGTMEvent = this.sendGTMEvent.bind(this);
        this.onNewAsset = this.onNewAsset.bind(this);
        this.onVideoStarted = this.onVideoStarted.bind(this);
        this.onVideoPlaying = this.onVideoPlaying.bind(this);
        this.onVideoProgress = this.onVideoProgress.bind(this);
        this.onVideoEnded = this.onVideoEnded.bind(this);
        this.onQuestionAsked = this.onQuestionAsked.bind(this);
        this.onQuestionAnsweredCorrectly = this.onQuestionAnsweredCorrectly.bind(this);
        this.onQuestionAnsweredIncorrectly = this.onQuestionAnsweredIncorrectly.bind(this);
        this.onOpenPDF = this.onOpenPDF.bind(this);
    }

    componentDidMount() {
        if (this.state.loaded) {
            var dataLayer = this.populateDataLayer(this.state.asset);
            if (!this.props.embedded)
                this.sendGTMEvent('customPageview', dataLayer);
        }
        var bestLanguage = this.getBestLanguage(this.props.asset, this.props.language);
        try {
            if (this.props.asset.englishTranscriptPath.endsWith(".xml")) {
                this.getXmlContent(bestLanguage);
            }
        }
        catch {
            //not xml
        }
    }

    componentDidUpdate(prevProps) {
        var bookmark = this.props.progressBookmark;

        if (this.playerRef && bookmark)
            this.playerRef.updateBookmark(bookmark);

        if (this.props.apiUrl && this.props.apiUrl !== prevProps.apiUrl) {
            this.loadData(bookmark);
            //clean up
        }

        if (this.state.isLoadingNewContent) {
            var bestLanguage = this.getBestLanguage(this.state.asset, this.props.language);
            try {
                if (this.state.asset.englishTranscriptPath.endsWith(".xml")) {
                    this.getXmlContent(bestLanguage);
                }
            }
            catch {
                //not xml
            }
            this.setState({ isLoadingNewContent: false });
        }
    }

    loadData(bookmark) {
        this.setState({
            loading: true,
            loaded: false
        });

        fetch(this.props.apiUrl)
            .then(response => response.json())
            .then(
                (response) => {
                    var result = response.assets[0];

                    this.setState({
                        loading: false,
                        loaded: true,
                        languageUrls: result.languageUrls,
                        asset: result.asset,
                        isLoadingNewContent: true
                    });

                    if (this.playerRef)
                        this.playerRef.updateIsci(result.asset.isci, this.getBestLanguage(result.asset, this.props.language), bookmark);

                    this.onNewAsset(result.asset);
                },
                (error) => {
                    this.setState({
                        loading: false,
                        loaded: false
                    });
                }
            );
    }

    setPlayerRef(ref) {
        this.playerRef = ref;
    }

    getBestLanguage(asset, preferredLanguage) {
        preferredLanguage = preferredLanguage || "en-US";
        if (asset.languages.indexOf(preferredLanguage) > -1)
            return preferredLanguage;
        else
            return asset.languages[0];
    }

    sendGTMEvent(eventName, clientSideDataLayer) {
        clientSideDataLayer = clientSideDataLayer || this.props.clientSideDataLayer;
        var serverSideDataLayer = this.props.serverSideDataLayer;

        clientSideDataLayer['event'] = eventName;

        this.props.dataLayer.push(Object.assign({}, serverSideDataLayer, clientSideDataLayer));
    }

    // TODO:  Shouldn't be relying on side effects here.
    populateDataLayer(asset) {
        var dataLayer = this.props.clientSideDataLayer;

        dataLayer['subject'] = asset.englishSubject;
        dataLayer['subjectTopic'] = asset.englishSubject + ' | ' + asset.englishTopic;
        dataLayer['assetId'] = asset.keyID;
        dataLayer['assetTitle'] = asset.englishTitle;
        dataLayer['assetType'] = asset.type;
        dataLayer['assetLanguage'] = this.props.languageNames[this.props.language || "en-US"];
        dataLayer['versionId'] = asset.isci;

        return dataLayer;
    }

    onNewAsset(asset) {
        var dataLayer = this.populateDataLayer(asset);

        if (!this.props.embedded)
            this.sendGTMEvent('customPageview', dataLayer);

        if (this.props.onNewAsset)
            this.props.onNewAsset(asset);
    }

    onVideoStarted() {
        this.sendGTMEvent('videoViewStart');

        if (this.props.onVideoStarted)
            this.props.onVideoStarted();
    }

    onVideoPlaying(currentTime, totalDuration) {
        this.playerRef.hideEndSlate();

        if (this.props.onVideoPlaying)
            this.props.onVideoPlaying(currentTime, totalDuration);
    }

    onVideoProgress(timeViewed, totalDuration, percentageViewed, beginningAtTime) {
        var clientSideDataLayer = this.props.clientSideDataLayer;

        clientSideDataLayer['videoTimeViewed'] = Math.round(timeViewed);
        clientSideDataLayer['videoPercentageViewed'] = Math.round(percentageViewed);

        this.sendGTMEvent('videoViewProgress');

        this.props.user.reportAssignmentProgress(
            this.props.clientSideDataLayer['assetId'], this.props.assignment, this.props.assignmentItem,
            Math.round(beginningAtTime), Math.round(timeViewed), Math.round(totalDuration)
        );

        if (this.props.onVideoProgress)
            this.props.onVideoProgress(timeViewed, totalDuration, percentageViewed, beginningAtTime);
    }

    onOpenPDF() {
        this.props.user.reportAssignmentProgress(this.props.clientSideDataLayer['assetId'], this.props.assignment, this.props.assignmentItem, 0.0, 1.0, 1.0);

        return true;
    }

    getXmlContent(bestLanguage) {
        let path = "";
        if (bestLanguage === "es-US") {
            path = this.state.asset.spanishTranscriptPath;
        } else {
            path = this.state.asset.englishTranscriptPath;
        }

        this.fetchData(path)
            .then(result => {
                this.setState({
                    xmlContent: result
                });
            })
            .catch(error => {
                console.error(error);
            });
    }
    /**
     * @param {number} id Content Type ID included in the XML file
     */
    isWebMDIgniteIPContent(id) {
        const contentTypeIds = [203, 3, 106, 209, 116, 207, 188, 82, 79];
        return contentTypeIds.includes(id)
    }

    /**
     * @param {number} id Content Type ID included in the XML file
     */
    shouldDisplayCopyrightFooter(id) {
        const fdbContentTypeId = 208;
        return id !== fdbContentTypeId;
    }

    fetchData(path) {
        return new Promise((resolve, reject) => {
            fetch(path)
                .then(response => response.text())
                .then(xmlString => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(xmlString, 'text/xml');
                    const contentObject = doc.getElementsByTagName("ContentObject")[0];
                    const copyright = doc.getElementsByTagName("CopyrightStatement")[0];
                    const contentTypeId = parseInt(contentObject.getAttribute("ContentTypeId"))
                    let copyrightWrapper = null;
                    if (this.shouldDisplayCopyrightFooter(contentTypeId)) {
                        if (this.isWebMDIgniteIPContent(contentTypeId)) {
                            const currentYear = new Date().getFullYear();
                            // Capture after the character ©, ignore anything and match a group of digits after "-".
                            // If this causes issues check commit 92f86c5a16bd4cb7c30c87815b58a214a9da0f06 for a simpler solution
                            const copyrightRegex = /©*-(\d+)/;
                            copyright.textContent = copyright.textContent.replace(copyrightRegex, `-${currentYear}`)
                        }
                        copyrightWrapper = document.createElement("section")
                        copyrightWrapper.className = "copyright"
                        copyrightWrapper.appendChild(copyright);
                    } else {
                        contentObject.removeChild(copyright);
                    }
                    const imgElements = doc.getElementsByTagName("img");
                    //need base path but FilePathRoot field doesn't directly exist in this.state.asset
                    const basePath = this.state.asset.metadataPath.substring(0, this.state.asset.metadataPath.indexOf("metadata.json"));
                    let imgUrl = "";
                    let imgId = "";
                    Array.from(imgElements).forEach(function (element) {
                        imgId = element.getAttribute("imageid");
                        imgUrl = basePath + imgId + ".jpg";
                        element.setAttribute("src", imgUrl);
                    });

                    const div = document.createElement('div');
                    const content = doc.getElementsByTagName("body")[0];
                    div.appendChild(content);
                    if (this.shouldDisplayCopyrightFooter(contentTypeId)) {
                        div.appendChild(copyrightWrapper);
                    }
                    resolve(div.innerHTML);
                })
                .catch(error => {
                    reject(error)
                });
        });
    }

    onVideoEnded() {
        this.sendGTMEvent('videoViewEnd');

        var nextAsset = this.props.nextAsset;

        this.playerRef.showEndSlate(
            React.createElement(VideoPlayerEndSlate, {
                copy: this.props.copy,
                upNextCountdown: this.props.endSlateUpNextCountdown,
                useRouter: this.props.endSlateUseRouter,
                question: this.props.question,
                answers: this.props.answers,
                correctAnswer: this.props.correctAnswer,
                onQuestionAsked: this.onQuestionAsked,
                onQuestionAnsweredCorrectly: this.onQuestionAnsweredCorrectly,
                onQuestionAnsweredIncorrectly: this.onQuestionAnsweredIncorrectly,
                showUpNext: this.props.showUpNext,
                answerFollowup: !this.props.answerFollowup ? null : () => {
                    return <div>
                        <div key="0">
                            Thank you for your feedback.
                        </div>
                        {
                            this.props.assignment.displayNextAssignment && this.props.assignment.displayNextAssignment.displayResumeItem ?
                                <div key="1">
                                    <AssetLink title="Continue to next Assignment" className="btn btn-outline-primary btn-lg my-3" assignment={this.props.assignment.displayNextAssignment} asset={this.props.assignment.displayNextAssignment.displayResumeItem.asset} urlHash={this.props.location.hash} />
                                </div> :
                                <div key="1">
                                    <div key="0">
                                        We have a great library! We invite you to check it out.
                                    </div>
                                    <a key="1" href={this.props.libraryUrl} className="btn btn-outline-primary btn-lg" role="button" aria-pressed="true">{this.props.copy.viewLibrary}</a>
                                </div>
                        }
                    </div>;
                },
                nextAsset:
                    nextAsset ?
                        {
                            url: nextAsset.urlWithHash || (nextAsset.url + this.props.location.hash),
                            asset: { title: nextAsset.asset.title }
                        } :
                        null
            }));

        if (this.props.onVideoEnded)
            this.props.onVideoEnded();
    }

    onQuestionAsked() {
        this.sendGTMEvent('videoQuestionAsked');

        if (this.props.onQuestionAsked)
            this.props.onQuestionAsked();
    }

    onQuestionAnsweredCorrectly() {
        this.sendGTMEvent('videoQuestionAnsweredCorrectly');

        this.props.user.reportAssessmentResult(this.props.assignment, this.props.assignmentItem, this.props.assessment, true);

        if (this.props.onQuestionAnsweredCorrectly)
            this.props.onQuestionAnsweredCorrectly();
    }

    onQuestionAnsweredIncorrectly() {
        this.props.user.reportAssessmentResult(this.props.assignment, this.props.assignmentItem, this.props.assessment, false);

        if (this.props.onQuestionAnsweredIncorrectly)
            this.props.onQuestionAnsweredIncorrectly();
    }

    render() {
        var bestLanguage = this.getBestLanguage(this.state.asset, this.props.language);
        var isXml = false;
        try {
            isXml = this.state.asset.englishTranscriptPath.endsWith(".xml");
        }
        catch {
            //not xml
        }

        var heading = [];

        if (!this.props.embedded) {
            heading.push(React.createElement('h1', { key: "h" }, this.state.asset.title));

            if (((this.state.asset.type === "PDF") || (this.state.asset.type === "Text")) && !isXml)
                heading.push(
                    React.createElement('a',
                        {
                            key: "p",
                            className: "open-pdf",
                            href: bestLanguage === "es-US" ? this.state.asset.spanishTranscriptPath : this.state.asset.englishTranscriptPath,
                            target: "_blank",
                            onClick: (e) => {
                                this.onOpenPDF();
                            }
                        },
                        this.props.copy.openDocument
                    ));
        }

        var transcripts = [];

        if (this.state.asset.type === "Video") {
            if (this.state.asset.englishTranscriptPath)
                transcripts.push(
                    React.createElement(
                        'a',
                        { key: "0", target: "_blank", href: this.state.asset.englishTranscriptPath, className: "transcript" + ((this.state.asset.spanishTranscriptPath) ? " separator" : "") },
                        'English'));

            if (this.state.asset.spanishTranscriptPath)
                transcripts.push(
                    React.createElement(
                        'a',
                        { key: "1", target: "_blank", href: this.state.asset.spanishTranscriptPath, className: "transcript" },
                        'Español'));
        }

        var languages = [];

        this.state.asset.languages.forEach(
            function (language, l, a) {
                languages.push(
                    React.createElement(
                        'a',
                        { key: l, href: this.state.languageUrls[language] + this.props.location.hash, className: (l < a.length - 1) ? "separator" : null },
                        this.props.languageNames[language]));
            }.bind(this));

        return React.createElement(
            'div',
            {
            },
            [
                (this.state.asset.type === "Text" || isXml) &&
                <AssetHeader asset={this.state.asset} embedded={this.props.embedded} copy={this.props.copy}  languages={languages}> {heading} </AssetHeader>,
                this.state.asset.type === "Video" ?
                    React.createElement(
                        'div',
                        {
                            key: "0",
                            className: 'player-container' + (this.props.embedded ? '' : ' mb-3'),
                            style: { position: this.props.embedded ? 'absolute' : 'relative', overflow: 'hidden', width: '100%', paddingBottom: this.props.embedded ? '0' : '56.25%', height: this.props.embedded ? '100%' : 'auto' }
                        },
                        React.createElement(
                            'div',
                            {
                            },
                            React.createElement(
                                VideoPlayer,
                                {
                                    asset: this.state.asset,
                                    language: bestLanguage,
                                    ref: this.setPlayerRef,
                                    autoPlay: this.props.autoPlayVideo,
                                    seekToBookmark: this.props.progressBookmark,
                                    onVideoStarted: this.onVideoStarted,
                                    onVideoPlaying: this.onVideoPlaying,
                                    onVideoProgress: this.onVideoProgress,
                                    onVideoEnded: this.onVideoEnded
                                })
                        )
                    ) :
                    this.state.asset.type === "3D Interactive" ?
                        React.createElement(
                            'div',
                            {
                                key: "0",
                                className: 'player-container mb-3',
                                style: { position: 'relative', overflow: 'hidden', width: '100%', paddingBottom: '56.25%', height: 'auto' }
                            },
                            React.createElement(
                                IframeViewer,
                                {
                                    metadataUrl: this.state.asset.metadataPath
                                })
                        ) :
                        React.createElement(
                            'div',
                            {
                                key: "0",
                                className: (this.props.embedded ? 'my-1' : ' mb-3')
                            },
                            this.state.asset.hasPosterImage ?
                                React.createElement(
                                    'a',
                                    {
                                        target: '_blank',
                                        href: bestLanguage === "es-US" ? this.state.asset.spanishTranscriptPath : this.state.asset.englishTranscriptPath
                                    },
                                    React.createElement(
                                        'img',
                                        {
                                            className: "poster", src: this.state.asset.posterImagePath
                                        }
                                    )
                                ) :
                                isXml ? React.createElement("div", { key: this.state.asset.isci, className: "xmlContent", dangerouslySetInnerHTML: { __html: this.state.xmlContent } }, null) : "Details"
                        ),
                (this.state.asset.type !== "Text" && !isXml) && React.createElement('div', { key: "1", className: 'heading' }, heading),
                React.createElement(
                    'div',
                    {
                        key: "2",
                        className: 'asset-metadata-container' + (this.props.embedded ? ' embedded' : '')
                    },
                    [
                        this.props.embedded ?
                            React.createElement(
                                'button',
                                {
                                    key: "0", className: 'info-button', 'aria-expanded': true, 'aria-controls': 'assetMetadata-collapse', type: 'button', 'data-target': '#assetMetadata-collapse', 'data-toggle': 'collapse', 'title': this.props.copy.aboutThisVideo
                                }) :
                            null,
                        React.createElement(
                            'div',
                            {
                                key: "1", id: 'assetMetadata-collapse', className: 'asset-metadata' + (this.props.embedded ? ' collapse' : '')
                            },
                            React.createElement(
                                'div',
                                {
                                    id: 'assetMetadata', className: 'description-languages-transcripts-share'
                                },
                                [
                                    this.props.embedded ?
                                        React.createElement(
                                            'div',
                                            {
                                                key: "0"
                                            },
                                            [
                                                React.createElement(
                                                    'h2',
                                                    { key: "h" },
                                                    this.props.copy.title),
                                                React.createElement(
                                                    'p',
                                                    { key: "p" },
                                                    this.state.asset.title)
                                            ]) :
                                        null,
                                    (this.state.asset.type !== "Text" && !isXml) && this.state.asset.description ?
                                        React.createElement(
                                            'div',
                                            {
                                                key: "1",
                                                className: (this.props.embedded ? 'my-1' : ' my-3')
                                            },
                                            React.createElement(
                                                'p',
                                                {},
                                                this.state.asset.description)
                                        ) :
                                        null,
                                    React.createElement(
                                        'div',
                                        {
                                            key: "2",
                                            className: 'languages-transcripts-share + ' + (this.props.embedded ? 'my-1' : ' my-3')
                                        },
                                        [
                                            React.createElement(
                                                'div',
                                                {
                                                    key: "0",
                                                    className: 'languages-share'
                                                },
                                                [
                                                    (this.state.asset.type !== "Text" && !isXml) && React.createElement(
                                                        'div',
                                                        {
                                                            key: "0",
                                                            className: 'd-flex flex-row flex-wrap languages'
                                                        },
                                                        [
                                                            React.createElement(
                                                                'h2',
                                                                { key: "h", className: "mt-2 mb-0" },
                                                                this.props.copy.availableLanguages + ':'),
                                                            React.createElement(
                                                                'div',
                                                                { key: "l", className: "d-flex flex-row flex-wrap mt-2 mb-0" },
                                                                languages
                                                            )
                                                        ]),
                                                    !this.props.embedded ?
                                                        (this.state.asset.type !== "Text" && !isXml) &&
                                                        React.createElement(
                                                            'div',
                                                            {
                                                                key: "1",
                                                                className: 'share'
                                                            },
                                                            React.createElement(
                                                                'a',
                                                                { className: "shareButton", href: "#", "data-toggle": "modal", "data-target": "#share-dialog" },
                                                                this.props.copy.share
                                                            )
                                                        ) :
                                                        null
                                                ]
                                            ),
                                            transcripts.length && !this.props.embedded ?
                                                React.createElement(
                                                    'div',
                                                    {
                                                        key: "1",
                                                        className: 'transcripts'
                                                    },
                                                    [
                                                        React.createElement(
                                                            'h2',
                                                            { key: "h" },
                                                            this.props.copy.availableTranscripts),
                                                        React.createElement(
                                                            'div',
                                                            { key: "t", className: "flex flex-row" },
                                                            transcripts
                                                        )
                                                    ]
                                                ) : null
                                        ]
                                    )
                                ]
                            )
                        )
                    ]
                )
            ]
        );
    }
}
